import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const PrivacyPolicy = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set document title and meta tags
    document.title = 'Privacy Policy | GrowthLink Digital Marketing';
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', "Read GrowthLink's privacy policy to understand how we collect, use, and protect your personal information. Compliant with Indian law and Telangana jurisdiction.");
    updateMetaTag('robots', 'noindex, nofollow');
    
    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/privacy-policy`);
  }, [siteUrl]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-darkGreen mb-6">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-12">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                Welcome to GrowthLink. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. This policy is governed by Indian law and falls under the jurisdiction of Telangana.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">2. Information We Collect</h2>
              <p className="leading-relaxed mb-3">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Personal identification information (Name, email address, phone number)</li>
                <li>Business information (Company name, industry, website URL)</li>
                <li>Communication data when you contact us</li>
                <li>Technical data (IP address, browser type, device information)</li>
                <li>Usage data (pages visited, time spent, navigation patterns)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-3">We use the collected information for various purposes:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>To provide and maintain our digital marketing services</li>
                <li>To notify you about changes to our services</li>
                <li>To provide customer support and respond to inquiries</li>
                <li>To analyze usage and improve our website and services</li>
                <li>To send marketing communications (with your consent)</li>
                <li>To detect, prevent and address technical issues</li>
                <li>To comply with legal obligations under Indian law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">4. Legal Basis for Processing (Indian Law)</h2>
              <p className="leading-relaxed mb-3">Under the Information Technology Act, 2000 and related rules, we process your personal data based on:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Your consent for specific purposes</li>
                <li>Performance of a contract with you</li>
                <li>Compliance with legal obligations</li>
                <li>Our legitimate business interests</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">5. Data Sharing and Disclosure</h2>
              <p className="leading-relaxed mb-3">We may share your information with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Service providers who assist in our operations</li>
                <li>Analytics and advertising partners</li>
                <li>Legal authorities when required by Indian law</li>
                <li>Business partners with your explicit consent</li>
              </ul>
              <p className="leading-relaxed mt-3">We do not sell your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">6. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">7. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by Indian law. Business records may be retained for up to 7 years as per Indian accounting and tax regulations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">8. Your Rights Under Indian Law</h2>
              <p className="leading-relaxed mb-3">You have the following rights regarding your personal data:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Right to access your personal information</li>
                <li>Right to correct inaccurate data</li>
                <li>Right to request deletion of your data</li>
                <li>Right to withdraw consent</li>
                <li>Right to lodge a complaint with authorities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">9. Cookies and Tracking Technologies</h2>
              <p className="leading-relaxed">
                We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. Please refer to our Cookie Policy for more details.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">10. Third-Party Links</h2>
              <p className="leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">11. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal data, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">12. Changes to This Privacy Policy</h2>
              <p className="leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">13. Jurisdiction</h2>
              <p className="leading-relaxed">
                This Privacy Policy shall be governed by and construed in accordance with the laws of India. Any disputes arising under or in connection with this Privacy Policy shall be subject to the exclusive jurisdiction of the courts in Telangana, India.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-darkGreen mb-4">14. Contact Us</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg space-y-2">
                <p><strong>GrowthLink</strong></p>
                <p>Email: privacy@growthlink.com</p>
                <p>Phone: +91 XXX XXX XXXX</p>
                <p>Address: Telangana, India</p>
              </div>
            </section>

            <section className="bg-accent/10 p-6 rounded-lg mt-12">
              <h3 className="text-xl font-semibold text-darkGreen mb-3">Compliance Statement</h3>
              <p className="leading-relaxed text-sm">
                This Privacy Policy complies with the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, and other applicable Indian data protection laws. GrowthLink is committed to maintaining the highest standards of data protection and privacy for all our clients and website visitors.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default PrivacyPolicy;
